import type { ImportProductItem, ImportVendorLookup, ParseImportCsvResult } from './types';

const normalizeText = (value: string) => value.replace(/^\uFEFF/, '').trim();

export const parsePrice = (raw: string): number | null => {
    const cleaned = raw.replace(/[^\d,.\-]/g, '').replace(',', '.');
    if (!cleaned) return null;

    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : null;
};

const isHeaderRow = (parts: string[]): boolean => {
    const article = parts[0]?.trim().toLowerCase() || '';
    const price = parts[parts.length - 2]?.trim().toLowerCase() || '';

    return article.includes('артикул') || price.includes('ціна') || price.includes('price');
};

const resolveVendor = (
    raw: string,
    vendors: ImportVendorLookup[],
): Pick<ImportProductItem, 'vendorId' | 'vendorName'> => {
    const vendorRaw = normalizeText(raw);
    if (!vendorRaw) {
        return { vendorId: null, vendorName: null };
    }

    if (/^\d+$/.test(vendorRaw)) {
        const vendorId = Number(vendorRaw);
        const vendor = vendors.find((v) => v.ID === vendorId);
        return {
            vendorId: vendorId,
            vendorName: vendor?.vendorName ?? null,
        };
    }

    const normalized = vendorRaw.toLowerCase();
    const vendor = vendors.find((v) => v.vendorName.trim().toLowerCase() === normalized);

    return {
        vendorId: vendor?.ID ?? null,
        vendorName: vendor?.vendorName ?? null,
    };
};

const parseLine = (line: string, vendors: ImportVendorLookup[]): ImportProductItem | null => {
    const parts = line.split(';').map((part) => part.trim());
    if (parts.length < 4) return null;

    const prodArticle = normalizeText(parts[0]);
    const vendorRaw = normalizeText(parts[parts.length - 1]);
    const prodDefPrice = parsePrice(parts[parts.length - 2]);
    const prodName = normalizeText(parts.slice(1, -2).join(';'));

    if (!prodArticle && !prodName) return null;

    const vendor = resolveVendor(vendorRaw, vendors);

    return {
        prodArticle,
        prodName,
        prodDefPrice,
        vendorRaw,
        line: 0,
        ...vendor,
    };
};

export const parseImportCsv = (content: string, vendors: ImportVendorLookup[] = []): ParseImportCsvResult => {
    const lines = content.replace(/^\uFEFF/, '').split(/\r?\n/);
    const items: ImportProductItem[] = [];
    let skipped = 0;

    lines.forEach((rawLine, index) => {
        const line = rawLine.trim();
        if (!line) return;

        const parts = line.split(';');

        if (index === 0 && isHeaderRow(parts)) return;

        const item = parseLine(line, vendors);
        if (item) {
            items.push({
                ...item,
                line: items.length + 1,
            });
        } else {
            skipped += 1;
        }
    });

    return { items, skipped };
};
